import React from 'react';
import ReactDOM from 'react-dom';
import storeManager from '../store/StoreManager';
import PreRenderView from '../components/preRender/PreRenderView';
import presignUrlService from './PresignUrlService';
import { reportError } from './ErrorReporting';
import environmentNames from '../environmentNames';
import objectTypes from '../constants/ObjectTypes';
import sessionReader from '../store/readHelpers/SessionReader';
import questionReader from '../store/readHelpers/QuestionReader';

let preRenderTarget;

/**
 * Gets the number of ms to wait between parsings. We need intervals to reduce the initial burden on slow clients.
 * Also, this helps to spread the load on the server, since all images in the question are added to the pre loading queue
 * after the parsing is done.
 *
 * @returns {number} milliseconds between 3 and 6 seconds
 */
function getShortWaitTime() {
    return Math.floor((Math.random() * 3000)) + 3000;
}

/**
 * @returns {number} milliseconds between 10 and 14 seconds
 */
function getLongWaitTime() {
    return Math.floor((Math.random() * 4000)) + 10000;
}

function createPreRenderTarget() {
    const document = window.document;
    const body = document.body;
    preRenderTarget = document.createElement('div');
    preRenderTarget.id = 'preRenderTarget';
    preRenderTarget.setAttribute('aria-hidden', 'true');
    preRenderTarget.style.cssText = 'position:relative;height:0px;width:0px;overflow:hidden;visibility:hidden';
    body.appendChild(preRenderTarget);
}

function prepareQuestionForPreRendering(question) {
    const questionClone = JSON.parse(JSON.stringify(question));
    const { mediaModels, responseModels } = questionClone;

    // targets of the models must get new targets
    if (responseModels) {
        responseModels.forEach((interaction) => {
            const target = interaction.target;
            const newTarget = `preload_${target}`;
            interaction.target = newTarget;
            // replace ="RESPONSE with ="preload_RESPONSE
            questionClone.componentBody = questionClone.componentBody.replace(`="${target}`, `="${newTarget}`);
        });
    }

    if (mediaModels) {
        mediaModels.forEach((mediaModel) => {
            const target = mediaModel.target;
            const newTarget = `preload_${target}`;
            mediaModel.target = newTarget;
            mediaModel.isPreRender = true;
            questionClone.componentBody = questionClone.componentBody.replace(`="${target}`, `="${newTarget}`);
        });
    }

    questionClone.identifier = `preload_${questionClone.identifier}`;
    questionClone.componentBody = questionClone.componentBody.replace('interactionWrapper_', 'preload_interactionWrapper_');

    return questionClone;
}

function preRenderQuestion(question) {
    if (questionReader.isCurrentlyShown(question)) {
        return;
    }

    if (!preRenderTarget) {
        createPreRenderTarget();
    }
    const questionForPreload = prepareQuestionForPreRendering(question);

    ReactDOM.render(
        <PreRenderView question={questionForPreload} />,
        preRenderTarget,
    );
}

function preRenderAndWaitALongTime(question, doneCallback) {
    preRenderQuestion(question);
    setTimeout(() => {
        doneCallback();
    }, getLongWaitTime());
}

function hasImageTagInItemBody(question) {
    const regex = /<img/gi;
    return regex.test(question.itemBody);
}

/**
 * Indicates that the question has assets that are fast loading and also can continue to load in
 * the background after the pre-render component for the question has been unmounted.
 * Questions only containing these assets and no slow loading can be pre-rendered quite quick.
 * @param question
 * @returns {boolean}
 */
function questionHasFastLoadingAssets(question) {
    const { extraAssets } = question;

    // Object types that currently don't get properly parsed and have a high likelihood of including media or mathquill
    if ([
        objectTypes.COMPOSITE,
        objectTypes.DRAG_DROP,
        objectTypes.EXTENDED_TEXT_MATH,
        objectTypes.GAP_MATCH,
        objectTypes.GRAPHIC_GAP_MATCH,
        objectTypes.GRAPHIC_TEXT_ENTRY,
        objectTypes.HOTSPOT,
        objectTypes.MATCHING,
        objectTypes.MATH_ENTRY].includes(question.objectType)) {
        return true;
    }

    const hasImageModel = question.mediaModels.some((mediaModel) => {
        return (mediaModel.type === 'image');
    });

    if (hasImageModel) {
        return true;
    }

    // extraAssets not detected as slow loading assets are regarded as fast loading
    if (extraAssets && extraAssets.length > 0) {
        return true;
    }

    return hasImageTagInItemBody(question);
}

/**
 * Indicates that the question will need some time to load finished and/or contains assets/media that will not
 * continue to load in background if question is unmounted.
 * Allow extra load time for these questions.
 * @param question
 * @returns {boolean}
 */
function questionHasSlowLoadingAssets(question) {
    const { PDFPanelModel, extraAssets, pciModuleConfiguration } = question;

    if (pciModuleConfiguration) {
        return true;
    }

    if (PDFPanelModel) {
        return true;
    }

    const hasSlowMediaModel = question.mediaModels.some((mediaModel) => {
        return (mediaModel.type === 'audio' || mediaModel.type === 'embed');
    });
    if (hasSlowMediaModel) {
        return true;
    }
    if (!extraAssets) {
        return false;
    }

    const hasSlowExtraAsset = extraAssets.some((assetType) => {
        return (['ckeditor', 'images', 'aceEditor'].indexOf(assetType) !== -1);
    });
    return hasSlowExtraAsset;
}

function questionNeedsUrlPresigning(question) {
    const { PDFPanelModel } = question;
    if (!PDFPanelModel || window.offlineMode || process.env.NODE_ENV === environmentNames.DEVELOPMENT) {
        return false;
    }
    const { presignedUrls } = storeManager.getState();
    const presignedUrlForPanel = presignedUrls[question.PDFPanelModel.logicalName];

    return !presignUrlService.isValidPresignedUrl(presignedUrlForPanel);
}

const preRenderService = {
    cleanUpPreRendering() {
        if (preRenderTarget) {
            ReactDOM.unmountComponentAtNode(preRenderTarget);
        }
    },

    handleQuestionPreRendering(question, doneCallback) {
        if (sessionReader.isParentTest()) {
            return;
        }

        if (questionReader.isCurrentlyShown(question)) {
            window.console.log('question being viewed, preload next');
            doneCallback();
            return;
        }

        if (questionNeedsUrlPresigning(question)) {
            window.console.log('needs presigning');
            presignUrlService.loadPresignedUrl(question.PDFPanelModel.logicalName, question.PDFPanelModel.pdfSourceUrl)
                .then(() => {
                    window.console.log('presign done, wait long time');
                    // the presigned url has loaded, then wait for it to render properly
                    preRenderAndWaitALongTime(question, doneCallback);
                })
                .catch((ex) => {
                    reportError(ex);
                    doneCallback();
                });
        } else if (questionHasSlowLoadingAssets(question)) {
            window.console.log('wait long time');
            // questions with audio, ckeditor and embed will need extra long load time and will be unpredictable
            preRenderAndWaitALongTime(question, doneCallback);
        } else if (questionHasFastLoadingAssets(question)) {
            window.console.log('wait short time');
            // questions with images and fast loading assets that will continue to load even if pre render component
            // is unmounted
            preRenderQuestion(question);
            setTimeout(() => {
                doneCallback();
            }, getShortWaitTime());
        } else {
            window.console.log('no pre rendering needed');
            setTimeout(() => {
                doneCallback();
            }, 500);
        }
    },
};

export default preRenderService;
